// Galeria do kit (`spec/tasks/WEB-50.md` item 3): o `Preview` de cada componente de
// `components/kit/*` com os MESMOS dados do croqui, para o screenshot lado a lado.
//
// Só em dev: `beforeLoad` derruba a rota em qualquer build que não seja `import.meta.env.DEV`,
// então a galeria nunca aparece em staging nem em produção.

import { createFileRoute, notFound } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { sellerNav } from '../app/nav-seller.ts';
import { Amount } from '../components/kit/amount.tsx';
import { Badge } from '../components/kit/badge.tsx';
import { Bars } from '../components/kit/bars.tsx';
import { Btn, IconBtn } from '../components/kit/btn.tsx';
import { CalendarMonth } from '../components/kit/calendar-month.tsx';
import { Card } from '../components/kit/card.tsx';
import { DateRangeFilter, rangeForPreset } from '../components/kit/date-range-filter.tsx';
import { DetailRow } from '../components/kit/detail-row.tsx';
import { Divider } from '../components/kit/divider.tsx';
import { Drawer } from '../components/kit/drawer.tsx';
import { Empty } from '../components/kit/empty.tsx';
import { Eyebrow } from '../components/kit/eyebrow.tsx';
import { Field } from '../components/kit/field.tsx';
import { Footnote } from '../components/kit/footnote.tsx';
import { ICON_NAMES, Icon } from '../components/kit/icon.tsx';
import { IconItem } from '../components/kit/icon-item.tsx';
import type { Ink } from '../components/kit/ink.ts';
import { Input } from '../components/kit/input.tsx';
import { Kbd } from '../components/kit/kbd.tsx';
import { KpiGrid } from '../components/kit/kpi-grid.tsx';
import { KV, KVGroup } from '../components/kit/kv.tsx';
import { ListToolbar } from '../components/kit/list-toolbar.tsx';
import { Mascot, Wordmark } from '../components/kit/mascot.tsx';
import { brl, brlSigned, pct, pctOf } from '../components/kit/money.ts';
import { Mono } from '../components/kit/mono.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { Num } from '../components/kit/num.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Pager } from '../components/kit/pager.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { Pill } from '../components/kit/pill.tsx';
import { ProfileCard } from '../components/kit/profile-card.tsx';
import { Progress } from '../components/kit/progress.tsx';
import { Rail } from '../components/kit/rail.tsx';
import { RevenueCard } from '../components/kit/revenue-card.tsx';
import { SearchInput } from '../components/kit/search-input.tsx';
import { Segmented } from '../components/kit/segmented.tsx';
import { Select } from '../components/kit/select.tsx';
import { Sidebar } from '../components/kit/sidebar.tsx';
import { Stat } from '../components/kit/stat.tsx';
import { type Col, Table } from '../components/kit/table.tsx';
import { IconTile, Tile } from '../components/kit/tile.tsx';
import { Timeline } from '../components/kit/timeline.tsx';
import { Toggle } from '../components/kit/toggle.tsx';
import { TwoLine } from '../components/kit/two-line.tsx';
import { TRANSACTION_STATUSES, TxStatusPill } from '../components/kit/tx-status.tsx';

const INKS: Ink[] = ['text', 'muted', 'faint', 'gold', 'green', 'danger'];

function Section({ name, children }: { name: string; children: ReactNode }) {
  return (
    <section data-kit={name} className="flex flex-col gap-3">
      <Eyebrow>{name}</Eyebrow>
      <div className="rounded-panel border border-border bg-panel p-5">{children}</div>
    </section>
  );
}

interface TxRow {
  id: string;
  payer: string;
  cents: number;
  status: string;
  at: string;
}

const TX_ROWS: TxRow[] = [
  { id: 'tx_01J9K2', payer: 'Carlos M.', cents: 12_990, status: 'paid', at: '14:32' },
  { id: 'tx_01J9K1', payer: 'Juliana S.', cents: 34_900, status: 'paid', at: '14:19' },
  { id: 'tx_01J9JZ', payer: 'Pedro A.', cents: 8990, status: 'chargeback', at: '13:58' },
];

const TX_COLS: Col<TxRow>[] = [
  {
    key: 'at',
    label: 'Hora',
    w: '70px',
    compact: true,
    render: (r) => <Mono ink="muted">{r.at}</Mono>,
  },
  { key: 'payer', label: 'Pagador', render: (r) => r.payer },
  {
    key: 'amount',
    label: 'Valor',
    w: '120px',
    align: 'right',
    compact: true,
    render: (r) => <Amount cents={r.cents} ink="text" />,
  },
  {
    key: 'status',
    label: 'Status',
    w: '130px',
    align: 'right',
    compact: true,
    render: (r) => <TxStatusPill status={r.status} />,
  },
];

function KitGallery() {
  const [seg, setSeg] = useState('paid');
  const [toggle, setToggle] = useState(true);
  const [range, setRange] = useState(rangeForPreset('30d'));
  const [calendar, setCalendar] = useState({ from: range.from, to: range.to });
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col gap-8 bg-bg p-8">
      <PageHeader
        title="Kit do croqui"
        sub="Preview de cada componente de components/kit"
        path="/dev/kit"
        size="lg"
      />

      <Section name="Amount">
        <div className="flex flex-wrap items-start gap-3">
          <Amount cents={12_990} />
          <Amount cents={-234} ink="muted" />
          <Amount cents={0} ink="faint" />
          <Amount cents={2_485_000} ink="gold" />
          <Amount cents={11_480} ink="green" sign="ledger" />
          <Amount cents={-8990} ink="danger" />
          <Amount cents={5_732_500} size={18} weight={700} ink="gold" />
        </div>
      </Section>

      <Section name="Badge">
        <div className="flex flex-wrap items-start gap-3">
          {INKS.map((k) => (
            <Badge key={k} ink={k}>
              {k === 'text' ? '3' : k}
            </Badge>
          ))}
          <Badge>transaction.reserve_released</Badge>
        </div>
      </Section>

      <Section name="Bars">
        <div className="flex w-[480px] flex-col gap-5">
          <Bars
            data={[
              { label: '07', value: 3900 },
              { label: '08', value: 7200 },
              { label: '09', value: 6800 },
              { label: '10', value: 8100 },
              { label: '11', value: 5225 },
            ]}
            highlight={4}
          />
          <Bars
            height={34}
            gap={3}
            labels={false}
            highlight={2}
            data={[
              { label: '<50', value: 64 },
              { label: '50 a 100', value: 131 },
              { label: '100 a 200', value: 152 },
              { label: '>500', value: 22 },
            ]}
          />
        </div>
      </Section>

      <Section name="Btn / IconBtn">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <Btn kind="primary" icon="arrow">
              Sacar
            </Btn>
            <Btn kind="card" icon="doc">
              Exportar
            </Btn>
            <Btn kind="ghost" icon="swap">
              Ver todas
            </Btn>
            <Btn kind="danger" icon="alert">
              Recusar
            </Btn>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Btn kind="primary" small icon="arrow">
              Sacar
            </Btn>
            <Btn kind="card" small>
              Ver transação
            </Btn>
            <Btn kind="card" disabled>
              Indisponível
            </Btn>
            <IconBtn icon="doc" label="Copiar id" />
            <IconBtn icon="alert" label="Falhou" ink="danger" />
            <IconBtn icon="copy" label="Copiar chave" ink="text" />
          </div>
        </div>
      </Section>

      <Section name="CalendarMonth">
        <CalendarMonth value={calendar} onSelect={setCalendar} months={2} />
      </Section>

      <Section name="Card">
        <div className="flex flex-wrap items-start gap-3">
          <Card className="w-50">
            <div className="text-13 font-semibold">Disponível</div>
            <div className="mt-1 text-12 text-muted">R$ 24.850,00</div>
          </Card>
          <Card active className="w-50">
            <div className="text-13 font-semibold">Linha selecionada</div>
            <div className="mt-1 text-12 text-muted">tx_01J9K2 · Pix pago</div>
          </Card>
        </div>
      </Section>

      <Section name="DateRangeFilter">
        <DateRangeFilter value={range} onChange={setRange} />
      </Section>

      <Section name="DetailRow">
        <div className="w-100">
          <DetailRow
            title="Pix recebido · Carlos M."
            sub="E2E a3f9… · 10/09 14:32"
            right={<Amount cents={12_990} ink="text" />}
          />
          <DetailRow
            title="Taxa"
            sub="account: platform_fees"
            right={<Amount cents={-234} ink="muted" />}
          />
          <DetailRow
            title="Estorno · MED 0021"
            sub="prazo 14/09 18h"
            right={<Amount cents={-8990} ink="danger" />}
          />
          <DetailRow
            last
            title={<span className="font-semibold">Disponível</span>}
            right={<Amount cents={2_485_000} size={18} weight={700} ink="gold" />}
          />
        </div>
      </Section>

      <Section name="Divider">
        <div className="flex w-90 flex-col gap-4">
          <span className="text-12 text-muted">Disponível</span>
          <Divider />
          <span className="text-12 text-muted">Reserva</span>
          <div className="flex h-12 items-stretch gap-3">
            <span className="text-12 text-muted">Pix</span>
            <Divider vertical />
            <span className="text-12 text-muted">Saque</span>
          </div>
        </div>
      </Section>

      <Section name="Drawer">
        <Btn
          kind="card"
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          Abrir drawer
        </Btn>
        <Drawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          title="Pix recebido"
          sub="led_01J9K2R3 · 11/09 14:32"
        >
          <KV k="account" v="available" monoV />
          <KV k="amount_cents" v={<Amount cents={11_480} ink="green" sign="ledger" />} />
          <KV k="memo" v="Carlos Mendes · taxa R$ 2,34" last />
        </Drawer>
      </Section>

      <Section name="Empty">
        <div className="flex flex-wrap items-start gap-3">
          <Empty title="Nenhum Pix hoje" className="flex-1" />
          <Empty
            className="flex-1"
            icon="swap"
            title="Nenhuma transação"
            hint="Crie uma cobrança ou aguarde o primeiro pagamento."
            action={
              <Btn kind="primary" small>
                Nova cobrança
              </Btn>
            }
          />
        </div>
      </Section>

      <Section name="Eyebrow">
        <div className="flex flex-wrap items-start gap-4">
          <Eyebrow>Geral</Eyebrow>
          <Eyebrow small>Líquido</Eyebrow>
          <Eyebrow className="text-gold">Faturamento</Eyebrow>
        </div>
      </Section>

      <Section name="Field / Input / Select">
        <div className="flex flex-wrap items-start gap-4">
          <Field label="Chave Pix" hint="CNPJ da loja, conferido no KYC" className="w-70">
            {(id) => (
              <Input id={id} defaultValue="12.345.678/0001-90" right={<Badge>CNPJ</Badge>} />
            )}
          </Field>
          <Field label="Webhook URL" className="w-70">
            {(id) => <Input id={id} placeholder="https://api.lojadorafa.com.br/hooks" />}
          </Field>
          <Field label="Status" className="w-45">
            {(id) => (
              <Select id={id} defaultValue="paid">
                {TRANSACTION_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Section>

      <Section name="Footnote">
        <div className="flex w-105 flex-col gap-2">
          <Footnote left="pagos · expirados" right="meta 90 %" />
          <Footnote left="<50" mid="faixa mais comum 100 a 200" right=">500" />
          <Footnote left="Σ débitos = Σ créditos" check="balanceado" />
        </div>
      </Section>

      <Section name="Icon">
        <div className="flex flex-wrap items-start gap-3">
          {ICON_NAMES.map((name) => (
            <div key={name} className="flex w-18 flex-col items-center gap-1.5">
              <Icon name={name} size={20} />
              <Mono size={10}>{name}</Mono>
            </div>
          ))}
        </div>
      </Section>

      <Section name="IconItem">
        <div className="flex w-105 flex-col gap-3">
          <IconItem
            icon="shield"
            ink="gold"
            title="Verificação: envie o contrato social"
            sub="kyc_status pending · saque bloqueado"
          />
          <IconItem
            icon="webhook"
            ink="danger"
            title="3 entregas de webhook falharam"
            sub="HTTP 502 · 3ª tentativa em 30 min"
            last
          />
          <IconItem
            tile={false}
            icon="lock"
            ink="gold"
            title="A taxa é congelada na venda."
            sub="Mudança de tabela não altera cobranças já pagas."
          />
        </div>
      </Section>

      <Section name="KV / KVGroup">
        <div className="flex flex-wrap items-start gap-6">
          <div className="w-70">
            <KV k="Webhooks entregues" v={<span className="text-green">98,7 %</span>} />
            <KV k="Latência média API" v="142 ms" />
            <KV k="mode" v="live" monoV last />
          </div>
          <KVGroup
            className="w-70"
            title="Referência"
            rows={[
              { k: 'reference.type', v: 'transaction', monoV: true },
              { k: 'reference.id', v: 'tx_01J9K2QH7V', monoV: true },
              { k: 'memo', v: 'Carlos Mendes · taxa R$ 2,34' },
            ]}
          />
        </div>
      </Section>

      <Section name="Kbd">
        <div className="flex flex-wrap items-start gap-3">
          <Kbd>⌘K</Kbd>
          <Kbd>Enter</Kbd>
          <Kbd>sk_live_</Kbd>
          <Kbd>verify-balances</Kbd>
        </div>
      </Section>

      <Section name="KpiGrid">
        <div className="flex w-130 flex-col gap-5">
          <KpiGrid
            items={[
              { k: 'Líquido', cents: 5_639_660 },
              { k: 'Taxas', cents: -92_840 },
              { k: 'Hoje', cents: 522_510 },
            ]}
          />
          <KpiGrid
            size={11}
            items={[
              { k: 'Bruto', cents: 5_732_500 },
              { k: 'Taxas', cents: -92_840 },
              { k: 'Estornos', cents: -131_240 },
              { k: 'Sacado', cents: -3_012_930 },
            ]}
          />
        </div>
      </Section>

      <Section name="ListToolbar">
        <ListToolbar
          tabs={[
            { id: 'all', label: 'Todas', count: 489 },
            { id: 'paid', label: 'Pagas', count: 447 },
            { id: 'pending', label: 'Aguardando', count: 3 },
          ]}
          value={seg}
          onChange={setSeg}
          tabsLabel="Filtro da lista"
          extra={
            <Btn kind="card" small icon="doc">
              CSV
            </Btn>
          }
          right={
            <>
              489 no período · <Amount cents={5_732_500} size={12} /> pagos
            </>
          }
        />
      </Section>

      <Section name="Mascot / Wordmark">
        <div className="flex flex-wrap items-end gap-4">
          {[64, 44, 32, 24].map((size) => (
            <Mascot key={size} size={size} />
          ))}
          <div className="text-19 font-bold tracking-[-0.01em]">
            <Wordmark />
          </div>
        </div>
      </Section>

      <Section name="money">
        <div className="flex w-130 flex-col gap-2 font-mono text-13">
          {[
            ['brl(12990)', brl(12_990)],
            ['brlSigned(12756)', brlSigned(12_756)],
            ['brlSigned(-1500)', brlSigned(-1500)],
            ['pct(149)', pct(149)],
            ['pctOf(10000, 149)', String(pctOf(10_000, 149))],
            ['brl(pctOf(10000, 149))', brl(pctOf(10_000, 149))],
          ].map(([expr, out]) => (
            <div key={expr} className="flex justify-between gap-4">
              <span className="text-muted">{expr}</span>
              <span className="tabular-nums">{out}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section name="Mono">
        <div className="flex flex-col gap-2.5">
          <Mono>tx_01J9K2QH7V</Mono>
          <Mono size={11} ink="text">
            /app/transactions
          </Mono>
          <Mono size={12} ink="gold">
            E2Ea3f9c1d2e7b04c9d81f0a6
          </Mono>
        </div>
      </Section>

      <Section name="Notice">
        <div className="flex flex-col gap-3">
          <Notice tone="ok" title="Saque de R$ 2.400,00 caiu na chave Pix.">
            Líquido após taxa de saque R$ 3,50.
          </Notice>
          <Notice
            tone="wait"
            icon="bolt"
            title="Mudança agendada para 01/10/2026 00:00."
            action={
              <Btn kind="card" small>
                Ver detalhes
              </Btn>
            }
          >
            Pix 1,49 % → 1,29 %. Vendas pagas antes disso mantêm a taxa de hoje.
          </Notice>
          <Notice tone="bad" title="Chargeback em tx_01J9JZ8R2P.">
            Débito de R$ 89,90 + taxa R$ 15,00 no extrato.
          </Notice>
          <Notice tone="neutral" icon="lock" title="Só vale para vendas pagas a partir da data." />
          <Notice tone="info" title="Sua tabela é negociada.">
            versão 3 · desde 01/08/2026
          </Notice>
        </div>
      </Section>

      <Section name="Num">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            {[18, 24, 32].map((size) => (
              <Num key={size} size={size}>
                R$ 129,90
              </Num>
            ))}
            <Num>R$ 57.325,00</Num>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            {INKS.map((k) => (
              <Num key={k} size={24} ink={k}>
                R$ 4.210,00
              </Num>
            ))}
          </div>
        </div>
      </Section>

      <Section name="PageHeader">
        <div className="flex flex-col gap-6">
          <PageHeader
            title="Transações"
            sub="Todas as cobranças da conta"
            path="/v1/me/transactions"
            actions={
              <Btn kind="card" small>
                Exportar
              </Btn>
            }
          />
          <PageHeader title="Visão geral" sub="Últimos 30 dias" size="lg" />
        </div>
      </Section>

      <Section name="Pager">
        <div className="flex w-140 flex-col">
          <Pager from={1} to={12} total={489} onNext={() => undefined} />
          <Pager from={1} to={3} total={3} />
        </div>
      </Section>

      <Section name="Panel">
        <div className="flex flex-col gap-4">
          <Panel>
            <div className="text-13 text-muted">Pix recebidos no período.</div>
          </Panel>
          <Panel
            title="Simule uma venda"
            sub="a mesma conta quando o Pix é confirmado"
            right={<Badge ink="gold">centavo</Badge>}
          >
            <div className="text-13">Cliente paga R$ 100,00 · líquido R$ 98,11</div>
          </Panel>
          <Panel n="01" title="Marca">
            <div className="text-13 text-muted">
              <Wordmark />
            </div>
          </Panel>
        </div>
      </Section>

      <Section name="Pill / TxStatus">
        <div className="flex flex-wrap items-start gap-2">
          <Pill tone="ok">Pago</Pill>
          <Pill tone="wait">Aguardando</Pill>
          <Pill tone="bad">Chargeback</Pill>
          <Pill tone="neutral">Expirado</Pill>
          <Pill tone="info">Sua tabela</Pill>
          {TRANSACTION_STATUSES.map((status) => (
            <TxStatusPill key={status} status={status} />
          ))}
        </div>
      </Section>

      <Section name="Progress">
        <div className="flex w-105 flex-col gap-3.5">
          <Progress pct={0} height={6} />
          <Progress pct={40} label="40% da meta" />
          <Progress pct={100} ink="green" label="concluído" />
        </div>
      </Section>

      <Section name="ProfileCard / RevenueCard">
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-59">
            <ProfileCard
              name="Rafael Moreira"
              email="rafael@lojadorafa.com.br"
              onLogout={() => undefined}
            />
          </div>
          <div className="w-59">
            <RevenueCard value="R$ 50.850,00" />
          </div>
          <div className="w-59">
            <RevenueCard value="R$ 50.850,00" goal="R$ 100.000,00" fill={51} />
          </div>
        </div>
      </Section>

      <Section name="SearchInput">
        <div className="flex w-140 flex-col gap-3">
          <SearchInput label="Buscar" />
          <SearchInput
            label="Buscar transação"
            placeholder="Buscar por E2E, id, nome, e-mail ou documento"
          />
          <SearchInput label="Buscar compacto" small className="w-70" />
        </div>
      </Section>

      <Section name="Segmented">
        <div className="flex flex-col items-start gap-3">
          <Segmented
            label="Período"
            items={[
              { id: 'today', label: 'Hoje' },
              { id: '7d', label: '7 dias' },
              { id: '30d', label: '30 dias' },
            ]}
            value="30d"
          />
          <Segmented
            label="Filtro da lista"
            items={[
              { id: 'all', label: 'Todas', count: 489 },
              { id: 'paid', label: 'Pagas', count: 447 },
              { id: 'pending', label: 'Aguardando', count: 3 },
            ]}
            value={seg}
            onChange={setSeg}
          />
          <Segmented
            label="Modo da conta"
            items={[
              { id: 'live', label: 'Produção', dot: 'green' },
              { id: 'test', label: 'Teste', dot: 'muted' },
            ]}
            value="live"
          />
        </div>
      </Section>

      <Section name="Sidebar / Rail">
        <div className="flex h-[900px] items-stretch gap-6 overflow-hidden">
          <Sidebar
            groups={sellerNav}
            activePath="/app/transactions"
            revenue={<RevenueCard value="R$ 50.850,00" />}
            footer={
              <ProfileCard
                name="Rafael Moreira"
                email="rafael@lojadorafa.com.br"
                onLogout={() => undefined}
              />
            }
            badges={{ withdrawals: 3, webhooks: 7 }}
          />
          <Rail groups={sellerNav} activePath="/app/transactions" />
        </div>
      </Section>

      <Section name="Stat">
        <div className="flex flex-wrap items-stretch gap-3">
          <div className="w-65">
            <Stat label="Disponível" value="R$ 18.420,00" />
          </div>
          <div className="w-65">
            <Stat
              label="Reserva"
              icon="lock"
              code="reserved"
              value="R$ 4.210,00"
              ink="muted"
              sub="libera em 7 dias"
            />
          </div>
          <div className="w-90">
            <Stat
              label="Vendas no período"
              icon="swap"
              iconInk="gold"
              code="sales.gross_cents"
              value="R$ 57.325,00"
              size="lg"
              ink="gold"
              sub="447 pagas · setembro"
              foot={
                <KpiGrid
                  items={[
                    { k: 'Líquido', cents: 5_639_660 },
                    { k: 'Taxas', cents: -92_840 },
                  ]}
                />
              }
            />
          </div>
        </div>
      </Section>

      <Section name="Table">
        <div className="flex flex-col gap-6">
          <Table
            cols={TX_COLS}
            rows={TX_ROWS}
            rowKey={(r) => r.id}
            onRow={() => undefined}
            selected={(r) => r.id === 'tx_01J9K1'}
          />
          <Table
            cols={TX_COLS}
            rows={[]}
            rowKey={(r) => r.id}
            emptyTitle="Nenhum Pix neste período."
          />
        </div>
      </Section>

      <Section name="Tile / IconTile">
        <div className="flex flex-col gap-5">
          <div className="flex items-end gap-3">
            {[28, 32, 34, 40, 48].map((size) => (
              <Tile key={size} size={size} className="text-13 font-bold text-gold">
                {size}
              </Tile>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            {INKS.map((k) => (
              <IconTile key={k} icon="ingot" ink={k} />
            ))}
            <IconTile icon="swap" size={28} iconSize={14} ink="green" />
            <IconTile icon="inbox" size={48} iconSize={22} />
          </div>
        </div>
      </Section>

      <Section name="Timeline">
        <div className="w-100">
          <Timeline
            items={[
              {
                at: '14:19',
                label: 'Cobrança criada',
                detail: 'R$ 129,90 · QR dinâmico',
                tone: 'info',
              },
              { at: '14:32', label: 'Pix pago', detail: 'E2E a3f9c1 · Carlos M.', tone: 'ok' },
              { at: '14:32', label: 'Taxa congelada', detail: '1,49 % + R$ 0,40 = R$ 2,34' },
              {
                at: '14:33',
                label: 'Webhook entregue',
                detail: 'transaction.paid · HTTP 200',
                tone: 'ok',
              },
            ]}
          />
        </div>
      </Section>

      <Section name="Toggle">
        <div className="flex flex-wrap items-start gap-4">
          <Toggle on={toggle} onChange={setToggle} label="Saque automático" />
          <Toggle on={false} label="Modo test" />
        </div>
      </Section>

      <Section name="TwoLine">
        <div className="flex gap-8 text-13">
          <TwoLine primary="14:32" secondary="11/09" mono />
          <TwoLine primary="Carlos Mendes" secondary="carlos.m@gmail.com" className="w-40" />
          <TwoLine primary="Sem secundária" />
        </div>
      </Section>
    </div>
  );
}

export const Route = createFileRoute('/dev/kit')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      throw notFound();
    }
  },
  component: KitGallery,
});
